export interface LeaderboardEntry {
  id: number;
  name: string;
  score: number;
  createdAt: string;
}

const localKey = "snappybird:local-leaderboard";

export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  try {
    const response = await fetch("/api/leaderboard", { headers: { accept: "application/json" } });
    if (!response.ok) {
      throw new Error(`Leaderboard fetch failed: ${response.status}`);
    }
    const payload = (await response.json()) as { entries: LeaderboardEntry[] };
    return payload.entries;
  } catch {
    return readLocalLeaderboard();
  }
}

export async function submitScore(name: string, score: number): Promise<LeaderboardEntry[]> {
  try {
    const response = await fetch("/api/leaderboard", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json"
      },
      body: JSON.stringify({ name, score })
    });

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(payload?.error ?? `Score submit failed: ${response.status}`);
    }

    const payload = (await response.json()) as { entries: LeaderboardEntry[] };
    return payload.entries;
  } catch {
    return writeLocalScore(name, score);
  }
}

function readLocalLeaderboard(): LeaderboardEntry[] {
  const raw = localStorage.getItem(localKey);
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw) as LeaderboardEntry[];
  } catch {
    localStorage.removeItem(localKey);
    return [];
  }
}

function writeLocalScore(name: string, score: number): LeaderboardEntry[] {
  const entries = readLocalLeaderboard();
  entries.push({
    id: Date.now(),
    name: sanitizeName(name),
    score,
    createdAt: new Date().toISOString()
  });

  const sorted = entries
    .sort((left, right) => right.score - left.score || left.createdAt.localeCompare(right.createdAt))
    .slice(0, 10);
  localStorage.setItem(localKey, JSON.stringify(sorted));
  return sorted;
}

function sanitizeName(name: string) {
  const cleaned = name.replace(/\p{C}/gu, "").trim();
  return cleaned.slice(0, 16) || "Pilot";
}
